import React from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest, signupRequest } from '../auth/authConfig';
import styled, { keyframes } from 'styled-components';
import { FaLock, FaUserPlus, FaArrowRight } from 'react-icons/fa';

const floatOrb1 = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  33%       { transform: translate(30px, -50px) scale(1.1); }
  66%       { transform: translate(-20px, 20px) scale(0.9); }
`;

const floatOrb2 = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  33%       { transform: translate(-40px, 30px) scale(0.9); }
  66%       { transform: translate(30px, -30px) scale(1.1); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const bounceIn = keyframes`
  0%   { opacity: 0; transform: scale(0.8); }
  60%  { transform: scale(1.05); }
  100% { opacity: 1; transform: scale(1); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 4px 20px rgba(255, 71, 87, 0.3); }
  50%       { transform: scale(1.03); box-shadow: 0 8px 32px rgba(255, 71, 87, 0.5); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const PageWrapper = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a0a2e 50%, #0d1117 100%);
  overflow: hidden;
  animation: ${fadeIn} 300ms ease forwards;
`;

const Orb = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
`;

const Orb1 = styled(Orb)`
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(255, 71, 87, 0.25) 0%, transparent 70%);
  top: -100px;
  right: -100px;
  animation: ${floatOrb1} 12s ease-in-out infinite;
`;

const Orb2 = styled(Orb)`
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(102, 51, 153, 0.2) 0%, transparent 70%);
  bottom: -150px;
  left: -150px;
  animation: ${floatOrb2} 15s ease-in-out infinite;
`;

const Orb3 = styled(Orb)`
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(26, 115, 232, 0.15) 0%, transparent 70%);
  top: 40%;
  left: 15%;
  animation: ${floatOrb1} 18s ease-in-out infinite reverse;
`;

const ContentBox = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 480px;
  padding: 0 24px;
  text-align: center;
`;

const Logo = styled.div`
  margin-bottom: 20px;
  animation: ${slideDown} 600ms cubic-bezier(0.22, 1, 0.36, 1) 300ms both;
`;

const Title = styled.h1`
  margin: 0 0 10px;
  color: #fff;
  font-size: 1.9rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  animation: ${slideDown} 600ms cubic-bezier(0.22, 1, 0.36, 1) 450ms both;
`;

const Subtitle = styled.p`
  margin: 0 0 36px;
  color: rgba(255, 255, 255, 0.55);
  font-size: 1rem;
  line-height: 1.5;
  max-width: 340px;
  animation: ${slideDown} 600ms cubic-bezier(0.22, 1, 0.36, 1) 550ms both;
`;

const ButtonStack = styled.div`
  width: 100%;
  max-width: 340px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  @media (max-width: 480px) { max-width: 100%; }
`;

// Primary = Create account (the action we want most people to take).
const CTAButton = styled.button`
  width: 100%;
  padding: 18px 32px;
  border: none;
  border-radius: 50px;
  background: linear-gradient(135deg, #ff4757, #ff6b35);
  color: white;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: opacity 0.2s, transform 0.2s;
  animation: ${bounceIn} 600ms cubic-bezier(0.22, 1, 0.36, 1) 700ms both,
             ${pulse} 2s ease-in-out 1400ms infinite;

  &:hover:not(:disabled) { opacity: 0.92; animation: none; transform: scale(1.02); }
  &:active:not(:disabled) { transform: scale(0.98); }
  &:disabled { cursor: default; animation: none; background: rgba(255, 255, 255, 0.1); }
`;

// Secondary = Sign in (visually distinct: outlined, calmer — so the two paths read differently).
const SecondaryButton = styled.button`
  width: 100%;
  padding: 15px 32px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 50px;
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.2s, border-color 0.2s, transform 0.15s;
  animation: ${fadeIn} 600ms ease 900ms both;

  &:hover:not(:disabled) { background: rgba(255, 255, 255, 0.09); border-color: rgba(255, 255, 255, 0.4); }
  &:active:not(:disabled) { transform: scale(0.98); }
  &:disabled { cursor: default; opacity: 0.5; }

  span { color: rgba(255, 255, 255, 0.5); font-weight: 500; }
`;

const TrustBadge = styled.div`
  position: absolute;
  bottom: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.3);
  font-size: 0.75rem;
  animation: ${fadeIn} 1s ease 1s both;

  svg {
    font-size: 0.8rem;
  }
`;

const Spinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  flex-shrink: 0;
`;

const LoginPage = () => {
  const { instance, inProgress } = useMsal();
  const isLoading = inProgress !== InteractionStatus.None;

  const startAuth = (request) => {
    if (inProgress !== InteractionStatus.None) return;
    // Belt-and-suspenders: clear any stale interaction lock from sessionStorage AND cookies
    // (storeAuthStateInCookie:true writes the lock to cookies too; iOS Safari persists them)
    Object.keys(sessionStorage)
      .filter(k => k.includes('interaction.status'))
      .forEach(k => sessionStorage.removeItem(k));
    document.cookie.split(';').forEach(c => {
      const name = c.trim().split('=')[0];
      if (name && name.includes('interaction.status')) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      }
    });
    // Fire-and-forget: loginRedirect navigates away, promise never resolves on success
    instance.loginRedirect(request).catch(e => {
      console.error('[MSAL] loginRedirect error:', e.errorCode, e.message);
    });
  };

  return (
    <PageWrapper>
      <Orb1 />
      <Orb2 />
      <Orb3 />
      <ContentBox>
        <Logo>
          <img src="/images/vusub-logo.svg" alt="VuSub" style={{ height: '72px', width: 'auto' }} />
        </Logo>
        <Title>Welcome to VuSub</Title>
        <Subtitle>New here? Create your account to start watching. Already have one? Sign in.</Subtitle>
        <ButtonStack>
          <CTAButton onClick={() => startAuth(signupRequest)} disabled={isLoading}>
            {isLoading ? (
              <>
                <Spinner />
                One moment&hellip;
              </>
            ) : (
              <>
                <FaUserPlus />
                Create account
              </>
            )}
          </CTAButton>
          <SecondaryButton onClick={() => startAuth(loginRequest)} disabled={isLoading}>
            <span>Already have an account?</span> Sign in <FaArrowRight />
          </SecondaryButton>
        </ButtonStack>
      </ContentBox>
      <TrustBadge>
        <FaLock />
        Secure sign-in powered by Azure
      </TrustBadge>
    </PageWrapper>
  );
};

export default LoginPage;
